import { taskNameValidator } from "@jugl-web/domain-resources/tasks";
import { useCreateTask } from "@jugl-web/domain-resources/tasks/hooks/useCreateTask";
import {
  INITIAL_FORM_STATE,
  TaskFormState,
} from "@jugl-web/domain-resources/tasks/hooks/useTaskFormState";
import { cx, useTranslations } from "@jugl-web/utils";
import { useTasksPageContext } from "@web-src/features/tasks/TasksPageContext";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import { FC, useRef, useState } from "react";
import { useOnboarding } from "@web-src/modules/preferences/providers";
import { ReactComponent as PlusIcon } from "../../assets/plus.svg";
import { TABLE_DIMENSIONS } from "../../consts";
import { TaskTableLayout } from "../TaskTableLayout";
import { TextCellEditor, TextCellEditorHandle } from "../TextCellEditor";

interface NewTaskRowProps {
  entityId: string;
  initialState: Partial<TaskFormState>;
}

export const NewTaskRow: FC<NewTaskRowProps> = ({ entityId, initialState }) => {
  const [isEditing, setIsEditing] = useState(false);

  const { taskListMode } = useTasksPageContext();

  const { navigateToPage } = useNavigation();
  const { isOnboardingActive, completeOnboardingStep } = useOnboarding();

  const { createTask, showTaskCreationToast, isLoading } = useCreateTask({
    entityId,
    isInTeamTasksContext: taskListMode === "team",
    onNavigate: (taskId) => navigateToPage("tasksDetails", { taskId }),
  });

  const $textCellEditorRef = useRef<TextCellEditorHandle | null>(null);

  const { t } = useTranslations();

  const handleSubmit = async (richText: string) => {
    try {
      const createdTask = await createTask({
        ...INITIAL_FORM_STATE,
        ...initialState,
        title: richText,
      });

      showTaskCreationToast(createdTask.id);
      if (isOnboardingActive) {
        completeOnboardingStep("task");
      }
      setIsEditing(false);
    } catch {
      // Do nothing
    }
  };

  if (!isEditing) {
    return (
      <TaskTableLayout.Row
        isHoverable
        height={TABLE_DIMENSIONS.TABLE_TASK_NEW_ITEM_ROW_HEIGHT}
        onClick={() => setIsEditing(true)}
      >
        <TaskTableLayout.OutermostEmptyCell />
        <TaskTableLayout.Cell shouldGrow>
          <div className="flex items-center gap-2.5">
            <PlusIcon />
            <TaskTableLayout.CellLabel>Add Task</TaskTableLayout.CellLabel>
          </div>
        </TaskTableLayout.Cell>
      </TaskTableLayout.Row>
    );
  }

  return (
    <TaskTableLayout.Row
      style={{
        minHeight: TABLE_DIMENSIONS.TABLE_TASK_ROW_HEIGHT,
        paddingBottom: TABLE_DIMENSIONS.TABLE_CELL_GAP,
      }}
    >
      <TaskTableLayout.OutermostEmptyCell />
      <TaskTableLayout.Cell
        isFocused
        width={TABLE_DIMENSIONS.TABLE_TASK_TITLE_COLUMN_WIDTH}
        className={cx(
          "justify-between",
          isLoading ? "cursor-not-allowed" : "cursor-text"
        )}
        onClick={() => $textCellEditorRef.current?.focus()}
      >
        <TextCellEditor
          ref={$textCellEditorRef}
          placeholder={t({
            id: "form-controls.task-title.placeholder-extended",
            defaultMessage: "Enter task title",
          })}
          isDisabled={isLoading}
          validator={taskNameValidator}
          onSubmit={handleSubmit}
          onCancel={() => setIsEditing(false)}
        />
      </TaskTableLayout.Cell>
      <TaskTableLayout.Cell shouldGrow />
    </TaskTableLayout.Row>
  );
};
